import { useMutation } from '@apollo/client';
import { CREATE_PROJECT_GROUP, UPDATE_PROJECT_GROUP } from 'apollo/mutations/project-group-mutations';
import { PROJECT_GROUPS_QUERY } from 'apollo/queries/project-group-queries';
import { useApolloPaging } from 'hooks';
import { DateTime } from 'luxon';
import ICursorable from 'types/Cursorable';
import { IProjectGroup, IProjectGroupCreateInput, IProjectGroupUpdateInput } from 'types/ProjectGroup';
import { getNowIsoAtUtcWithLocalTimeZone } from 'utils/dateUtils';
import uuid from 'uuid';

export default function useProjectGroup() {
  const { getAll } = useApolloPaging();
  const [createProjectGroupMutation] = useMutation(CREATE_PROJECT_GROUP);
  const [updateProjectGroupMutation] = useMutation(UPDATE_PROJECT_GROUP);

  async function getProjectGroups(groupId: string | null) {
    const results = await getAll<IProjectGroup & ICursorable>('projectGroups', {
      query: PROJECT_GROUPS_QUERY,
      variables: {
        first: 500,
        filter: {
          id: groupId ? { equal: groupId } : undefined,
          deletedOn: { isNull: true },
        },
      },
      fetchPolicy: 'network-only',
    });

    return results;
  }

  async function createProjectGroup(organizationId: string, groupName: string) {
    const createOnUtc = getNowIsoAtUtcWithLocalTimeZone();
    const group: IProjectGroupCreateInput = {
      id: uuid(),
      organizationId,
      groupName,
      createdOn: createOnUtc,
    };
    return new Promise<IProjectGroup>((resolve, reject) => {
      return createProjectGroupMutation({ variables: { projectGroup: group } }).then((result) => {
        const projectGroup = result.data?.createProjectGroup;
        if (projectGroup) {
          resolve(projectGroup);
        } else {
          reject();
        }
      });
    });
  }

  async function updateProjectGroup(projectGroup: IProjectGroup, groupName: string | null) {
    const group: IProjectGroupUpdateInput = {
      id: projectGroup.id,
      groupName,
    };
    return new Promise<IProjectGroup>((resolve, reject) => {
      return updateProjectGroupMutation({ variables: { projectGroup: group } }).then((result) => {
        const projectGroup = result.data?.updateProjectGroup;
        if (projectGroup) {
          resolve(projectGroup);
        } else {
          reject();
        }
      });
    });
  }

  async function deleteProjectGroup(projectGroupId: string) {
    const group: IProjectGroupUpdateInput = {
      id: projectGroupId,
      deletedOn: DateTime.utc().toISO(),
    };
    return new Promise<IProjectGroup>((resolve, reject) => {
      return updateProjectGroupMutation({ variables: { projectGroup: group } }).then((result) => {
        const projectGroup = result.data?.updateProjectGroup;
        if (projectGroup) {
          resolve(projectGroup);
        } else {
          reject();
        }
      });
    });
  }

  return { getProjectGroups, createProjectGroup, updateProjectGroup, deleteProjectGroup };
}
